<template>
  <div class="detail-container">
    <!-- 顶部导航 -->
    <TopHeader :fixed="true" />

    <!-- 统计信息 -->
    <div class="stats-bar">
      <div class="stats-content">
        <div class="stat-item">
          <span class="stat-icon correct-icon">✓</span>
          <span class="stat-value">{{ correctPercent }}%</span>
          <span class="stat-label">正确率</span>
        </div>
        <div class="stat-item">
          <span class="stat-icon score-icon">↺</span>
          <span class="stat-value">{{ record.score || 0 }}分</span>
          <span class="stat-label">分数</span>
        </div>
        <div class="stat-item">
          <span class="stat-icon time-icon">○</span>
          <span class="stat-value">{{ formatUsedTime }}</span>
          <span class="stat-label">用时</span>
        </div>
      </div>
    </div>

    <!-- 主内容区 -->
    <div class="main-wrapper">
      <div class="content-card">
        <!-- 加载中 -->
        <div v-if="loading" class="loading-box">加载中...</div>

        <!-- 题目列表 -->
        <div v-else class="question-list">
          <div
            v-for="(q, index) in answers"
            :key="index"
            :id="'question-' + index"
            class="question-item"
          >
            <div class="question-header">
              <span class="question-title">{{ index + 1 }}.{{ q.questionTitle }}</span>
              <div class="question-right">
                <span :class="['question-type', q.questionType === '1' ? 'single' : 'multi']">
                  {{ q.questionType === '1' ? '单选题' : '多选题' }}
                </span>
                <img 
                  :src="q.isFavorite ? require('@/assets/image/star-select.png') : require('@/assets/image/star.png')" 
                  class="favorite-icon"
                  @click="toggleFavorite(q)"
                />
              </div>
            </div>
            <div class="options">
              <div
                v-for="(opt, optIndex) in q.options"
                :key="optIndex"
                :class="getOptionClass(q, opt)"
              >
                <span :class="['option-label', q.questionType === '1' ? 'circle' : 'square']">{{ opt.label }}</span>
                <span v-if="opt.content" class="option-text">{{ opt.content }}</span>
                <img v-else-if="opt.image" :src="baseURL + opt.image" class="option-image" @click="showPreview(baseURL + opt.image)" />
              </div>
            </div>
            <!-- 答案解析 -->
            <div class="answer-section">
              <div class="answer-info">
                <span class="answer-label">正确答案：{{ q.correctAnswer }}</span>
                <span class="your-answer" :class="{ wrong: q.isCorrect !== '1' }">
                  你的答案：{{ q.userAnswer || '未作答' }}
                </span>
                <span class="get-score">得分：{{ q.score }}分</span>
              </div>
              <div v-if="q.analysis" class="answer-explain">
                {{ q.analysis }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 回到顶部 -->
      <div class="back-top" @click="scrollToTop">
        <img src="@/assets/image/up.png" class="arrow-icon" />
        <span>顶部</span>
      </div>
    </div>

    <!-- 底部答题卡 -->
    <div :class="['footer-bar', { expanded: showAnswerCard }]">
      <div class="footer-content">
        <div class="answer-card-toggle" @click="showAnswerCard = !showAnswerCard">
          <img :src="showAnswerCard ? require('@/assets/image/down.png') : require('@/assets/image/up.png')" class="arrow-icon" />
          <span>答题卡</span>
        </div>
        <div v-if="showAnswerCard" class="answer-stats">
          <span class="stat-item correct">
            <span class="stat-dot"></span>
            正确（{{ correctCount }}）
          </span>
          <span class="stat-item wrong">
            <span class="stat-dot"></span>
            错误（{{ wrongCount }}）
          </span>
        </div>
        <div v-else class="pagination-row">
          <span
            v-for="(q, index) in answers"
            :key="index"
            :class="['page-num', q.isCorrect === '1' ? 'correct' : 'wrong']"
            @click="scrollToQuestion(index)"
          >{{ index + 1 }}</span>
        </div>
      </div>
      <div v-if="showAnswerCard" class="answer-card-grid">
        <span
          v-for="(q, index) in answers"
          :key="index"
          :class="['grid-num', q.isCorrect === '1' ? 'correct' : 'wrong']"
          @click="scrollToQuestion(index)"
        >{{ index + 1 }}</span>
      </div>
    </div>

    <!-- 图片预览弹窗 -->
    <div v-if="previewImage" class="image-preview-overlay" @click="previewImage = ''">
      <img :src="previewImage" class="preview-img" @click.stop />
    </div>
  </div>
</template>

<script>
import TopHeader from '@/components/TopHeader.vue'
import { getExamRecordDetail } from '@/api/exam'
import { toggleFavorite as toggleFavoriteApi, checkFavorites } from '@/api/question'
import { decodeId } from '@/utils/idEncoder'
import config from '@/utils/config'

export default {
  name: 'ExamRecordDetailPage',
  components: {
    TopHeader
  },
  data() {
    return {
      baseURL: config.imageBaseURL,
      recordId: null,
      loading: true,
      record: {},
      answers: [],
      showAnswerCard: false,
      previewImage: ''
    }
  },
  computed: {
    correctCount() {
      return this.answers.filter(a => a.isCorrect === '1').length
    },
    wrongCount() {
      return this.answers.filter(a => a.isCorrect !== '1').length
    },
    correctPercent() {
      if (this.answers.length === 0) return 0
      return Math.round((this.correctCount / this.answers.length) * 100)
    },
    formatUsedTime() {
      const usedTime = this.record.usedTime || 0
      const minutes = Math.floor(usedTime / 60)
      const seconds = usedTime % 60
      if (minutes > 0) {
        return `${minutes}分钟`
      }
      return `${seconds}秒`
    }
  },
  created() {
    this.recordId = decodeId(this.$route.params.recordId)
    if (this.recordId) {
      this.loadDetail()
    } else {
      this.$router.push('/exam-record')
    }
  },
  methods: {
    async loadDetail() {
      this.loading = true
      try {
        const res = await getExamRecordDetail(this.recordId)
        if (res.code === 200) {
          this.record = res.data.record || {}
          this.answers = res.data.answers || []

          // 批量检查收藏状态
          if (this.answers.length > 0) {
            const questionIds = this.answers.map(a => a.questionId)
            const favRes = await checkFavorites(questionIds)
            if (favRes.code === 200) {
              const favoriteIds = favRes.data || []
              this.answers.forEach(a => {
                this.$set(a, 'isFavorite', favoriteIds.includes(a.questionId))
              })
            }
          }
        } else {
          alert(res.msg || '加载失败')
          this.$router.push('/exam-record')
        }
      } catch (error) {
        console.error('加载详情失败', error)
        alert('加载失败')
        this.$router.push('/exam-record')
      } finally {
        this.loading = false
      }
    },
    async toggleFavorite(q) {
      try {
        const res = await toggleFavoriteApi(q.questionId)
        if (res.code === 200) {
          this.$set(q, 'isFavorite', res.data)
        } else {
          alert(res.msg || '操作失败')
        }
      } catch (error) {
        console.error('收藏操作失败', error)
        alert('操作失败')
      }
    },
    goBack() {
      this.$router.push('/exam-record')
    },
    getOptionClass(question, option) {
      const classes = ['option']
      const userAnswers = question.userAnswer ? question.userAnswer.split(',') : []
      const correctAnswers = question.correctAnswer ? question.correctAnswer.split(',') : []
      const isUserSelected = userAnswers.includes(option.label)
      const isCorrect = correctAnswers.includes(option.label)

      if (isCorrect) {
        classes.push('correct')
      }
      if (isUserSelected && !isCorrect) {
        classes.push('wrong')
      }
      return classes
    },
    scrollToTop() {
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
    },
    scrollToQuestion(index) {
      const el = document.getElementById('question-' + index)
      if (el) {
        const headerHeight = 130
        const top = el.offsetTop - headerHeight
        document.documentElement.scrollTop = top
        document.body.scrollTop = top
      }
    },
    showPreview(url) {
      this.previewImage = url
    }
  }
}
</script>

<style scoped>
.detail-container {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 70px;
  padding-top: 52px;
}

.stats-bar {
  background: #f5f5f5;
  padding: 30px 20px;
}

.stats-content {
  max-width: 600px;
  margin: 0 auto;
  display: flex;
  justify-content: space-around;
  align-items: center;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
}

.stat-icon {
  font-size: 20px;
  margin-bottom: 5px;
}

.correct-icon {
  color: #f5a623;
}

.score-icon {
  color: #3d8c40;
}

.time-icon {
  color: #999;
}

.stat-value {
  font-size: 24px;
  font-weight: 600;
  color: #333;
}

.stat-label {
  font-size: 13px;
  color: #999;
}

.main-wrapper {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 20px;
}

.content-card {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  min-height: 300px;
}

.loading-box {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: #999;
  font-size: 14px;
}

.question-list {
  padding: 0 25px;
}

.question-item {
  padding: 25px 0;
  border-bottom: 1px solid #f0f0f0;
}

.question-item:last-child {
  border-bottom: none;
}

.question-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 15px;
  margin-bottom: 18px;
}

.question-title {
  flex: 1;
  font-size: 15px;
  color: #333;
  line-height: 1.6;
}

.question-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.question-type {
  font-size: 14px;
  font-weight: 500;
}

.question-type.single {
  color: #3d8c40;
}

.question-type.multi {
  color: #e74c3c;
}

.favorite-icon {
  width: 20px;
  height: 20px;
  cursor: pointer;
  flex-shrink: 0;
}

.question-score {
  font-size: 13px;
  color: #999;
}

.result-tag {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
}

.result-tag.correct {
  background: #d4edda;
  color: #3d8c40;
}

.result-tag.wrong {
  background: #f8d7da;
  color: #e74c3c;
}

.options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 6px;
  background: #fff;
  border: 1px solid #e8e8e8;
  min-height: 54px;
  box-sizing: border-box;
}

.option.correct {
  background: #d4edda;
  border-color: #3d8c40;
}

.option.correct .option-label {
  background: #3d8c40;
  color: #fff;
  border-color: #3d8c40;
}

.option.wrong {
  background: #f8d7da;
  border-color: #e74c3c;
}

.option.wrong .option-label {
  background: #e74c3c;
  color: #fff;
  border-color: #e74c3c;
}

.option-label {
  width: 26px;
  height: 26px;
  border: 2px solid #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: #666;
  flex-shrink: 0;
}

.option-label.circle {
  border-radius: 50%;
}

.option-label.square {
  border-radius: 4px;
}

.option-text {
  font-size: 14px;
  color: #333;
  line-height: 1.5;
}

.option-image {
  height: 40px;
  width: auto;
  object-fit: contain;
  border-radius: 4px;
  cursor: pointer;
}

.answer-section {
  margin-top: 20px;
  padding: 15px;
  background: #fafafa;
  border-left: 3px solid #3d8c40;
  border-radius: 0 6px 6px 0;
}

.answer-info {
  display: flex;
  gap: 20px;
  margin-bottom: 12px;
  font-size: 13px;
  flex-wrap: wrap;
}

.answer-label {
  color: #3d8c40;
}

.your-answer {
  color: #666;
}

.your-answer.wrong {
  color: #e74c3c;
}

.get-score {
  color: #666;
}

.answer-explain {
  font-size: 13px;
  color: #666;
  line-height: 1.8;
}

.back-top {
  position: fixed;
  right: 30px;
  bottom: 100px;
  padding: 10px 15px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 4px;
}

.arrow-icon {
  width: 12px;
  height: 12px;
}

.footer-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  border-top: 1px solid #e8e8e8;
  transition: all 0.3s;
}

.footer-bar.expanded {
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
}

.footer-content {
  max-width: 900px;
  margin: 0 auto;
  padding: 15px 25px;
  display: flex;
  align-items: center;
  gap: 25px;
}

.answer-card-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  color: #666;
  flex-shrink: 0;
}

.answer-stats {
  display: flex;
  align-items: center;
  gap: 30px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #333;
}

.stat-dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
}

.stat-item.correct .stat-dot {
  background: #3d8c40;
}

.stat-item.wrong .stat-dot {
  background: #e74c3c;
}

.answer-card-grid {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 25px 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  max-height: 200px;
  overflow-y: auto;
}

.grid-num {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  cursor: pointer;
  background: #f5f5f5;
  color: #666;
  flex-shrink: 0;
}

.grid-num.correct {
  background: #3d8c40;
  color: #fff;
}

.grid-num.wrong {
  background: #e74c3c;
  color: #fff;
}

.pagination-row {
  display: flex;
  gap: 10px;
  flex: 1;
  overflow: hidden;
  height: 30px;
  mask-image: linear-gradient(to right, black calc(100% - 40px), transparent 100%);
  -webkit-mask-image: linear-gradient(to right, black calc(100% - 40px), transparent 100%);
}

.page-num {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  cursor: pointer;
  background: #f5f5f5;
  color: #666;
  flex-shrink: 0;
}

.page-num.correct {
  background: #3d8c40;
  color: #fff;
}

.page-num.wrong {
  background: #e74c3c;
  color: #fff;
}

/* 图片预览弹窗 */
.image-preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.preview-img {
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
  border-radius: 8px;
}

/* 移动端适配 */
@media screen and (max-width: 768px) {
  .stats-content {
    gap: 20px;
  }

  .stat-value {
    font-size: 20px;
  }

  .main-wrapper {
    padding: 0 10px;
  }

  .question-list {
    padding: 0 15px;
  }

  .question-item {
    padding: 20px 0;
  }

  .answer-info {
    flex-direction: column;
    gap: 8px;
  }

  .footer-content {
    padding: 12px 15px;
    gap: 15px;
    flex-wrap: wrap;
  }

  .answer-stats {
    gap: 15px;
    flex-wrap: wrap;
  }

  .answer-card-grid {
    padding: 0 15px 15px;
    gap: 8px;
    max-height: 150px;
  }

  .grid-num {
    width: 32px;
    height: 32px;
    font-size: 13px;
  }

  .page-num {
    width: 26px;
    height: 26px;
    font-size: 12px;
  }

  .pagination-row {
    height: 26px;
  }

  .back-top {
    right: 15px;
    bottom: 80px;
  }
}
</style>
